import { Client } from "@stomp/stompjs";

export interface NotificationMessage {
  id: string;
  // type: "campagne" | "commande" | "utilisateur";
  type: string;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
}

export interface WebSocketSubscription {
  topic: string;
  enabled: boolean;
}

// Type pour les callbacks de notification
export type NotificationCallback = (message: NotificationMessage) => void;

class WebSocketService {
  private client: Client | null = null;
  private subscriptions: Map<string, any> = new Map();
  private messageCallbacks: Map<string, NotificationCallback> = new Map();
  private globalNotificationCallback: NotificationCallback | null = null;
  private isConnected = false;
  private userEmail: string = "";
  private pendingSubscriptions: WebSocketSubscription[] = [];

  constructor() {
    this.initializeClient();
  }

  // Méthode pour définir le callback global des notifications
  setGlobalNotificationCallback(callback: NotificationCallback) {
    this.globalNotificationCallback = callback;
  }

  private initializeClient() {
    this.client = new Client({
      brokerURL: import.meta.env.VITE_WS_API_URL + "/public/notifications",
      connectHeaders: {},
      debug: (str) => {
        // console.log("STOMP Debug: ", str);
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      // Ajouter ces options pour améliorer la stabilité
      forceBinaryWSFrames: true,
      appendMissingNULLonIncoming: true,
    });

    this.client.onConnect = (frame) => {
      this.isConnected = true;
      // Réabonner aux topics existants après reconnexion
      this.resubscribeToExistingTopics();
    };

    this.client.onDisconnect = (receipt) => {
      this.isConnected = false;
    };

    this.client.onStompError = (frame) => {
      this.isConnected = false;
    };

    this.client.onWebSocketError = (error) => {
      this.isConnected = false;
    };

    this.client.onWebSocketClose = (event) => {
      this.isConnected = false;
    };
  }

  connect() {
    if (this.client && !this.isConnected) {
      this.client.activate();
    }
  }

  disconnect() {
    if (this.client && this.isConnected) {
      this.client.deactivate();
    }
  }

  private resubscribeToExistingTopics() {
    if (this.userEmail && this.pendingSubscriptions.length > 0) {
      this.pendingSubscriptions.forEach(({ topic, enabled }) => {
        if (enabled && this.messageCallbacks.has(topic)) {
          const callback = this.messageCallbacks.get(topic)!;
          this.subscribeToTopic(this.userEmail, topic, callback);
        }
      });
    }
  }

  subscribeToTopic(
    userEmail: string,
    topic: string,
    callback: NotificationCallback
  ) {
    // Stocker l'email utilisateur et le callback
    this.userEmail = userEmail;
    this.messageCallbacks.set(topic, callback);

    if (!this.client) {
      // console.warn("WebSocket client not initialized");
      return;
    }

    if (!this.isConnected) {
      // console.warn("WebSocket not connected, attempting to connect...");
      this.connect();
      // Attendre un peu que la connexion s'établisse
      setTimeout(() => {
        this.attemptSubscription(userEmail, topic, callback);
      }, 1000);
      return;
    }

    this.attemptSubscription(userEmail, topic, callback);
  }

  private getTypeTitle(chaine?: string): string {
    if (!chaine) return "";
    const regex = /\[([A-Z]+)\]/;
    const match = chaine.match(regex);
    return match && match[1] ? match[1] : "";
  }

  private getTypeId(chaine?: string): string | null {
    if (!chaine) return null;
    const regex = /^[^-]+-[^-]+-[^-]+-[^-]+-[^-]+/;
    const match = chaine.match(regex);
    return match && match[0] ? match[0] : null;
  }

  public messageParser(data?: string): NotificationMessage | null {
    if (!data) return null;

    try {
      const [TypeNotification, message] = data.split(":");
      const type = this.getTypeTitle(TypeNotification);
      const typeID = this.getTypeId(TypeNotification);
      return {
        id: typeID || Date.now().toString(),
        type: type || "Notification",
        title: `Nouvelle ${type || "Notification"}`,
        message: message || "Pas de message fourni",
        timestamp: new Date(Date.now()),
        read: false,
      };
    } catch (error) {
      return null;
    }
  }

  private attemptSubscription(
    userEmail: string,
    topic: string,
    callback: NotificationCallback
  ) {
    if (!this.client || !this.isConnected) {
      return;
    }

    const subscriptionTopic = `/user/${userEmail}${topic}`;

    try {
      // Désabonner l'ancien topic s'il existe
      if (this.subscriptions.has(topic)) {
        this.subscriptions.get(topic).unsubscribe();
      }

      // S'abonner au nouveau topic
      const subscription = this.client.subscribe(
        subscriptionTopic,
        (message) => {
          try {
            const notificationData = this.messageParser(message.body);
            const notification: NotificationMessage = {
              id:
                notificationData?.id ||
                `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: topic.replace("/", ""),
              title: notificationData?.title || "Notification",
              message: notificationData?.message || message.body,
              timestamp: new Date(notificationData?.timestamp || Date.now()),
              read: false,
            };

            // Appeler le callback spécifique au topic
            callback(notification);

            // Appeler le callback global s'il existe (pour persister dans le store)
            if (this.globalNotificationCallback) {
              this.globalNotificationCallback(notification);
            }
          } catch (error) {
            // Fallback pour les messages texte simples
            const fallbackNotification: NotificationMessage = {
              id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: topic.replace("/", ""),
              title: "Notification",
              message: message.body,
              timestamp: new Date(),
              read: false,
            };

            callback(fallbackNotification);

            if (this.globalNotificationCallback) {
              this.globalNotificationCallback(fallbackNotification);
            }
          }
        }
      );

      this.subscriptions.set(topic, subscription);
    } catch (error) {
      // console.error("Error subscribing to topic:", error);
    }
  }

  unsubscribeFromTopic(topic: string) {
    if (this.subscriptions.has(topic)) {
      this.subscriptions.get(topic).unsubscribe();
      this.subscriptions.delete(topic);
      this.messageCallbacks.delete(topic);
    }
  }

  updateSubscriptions(
    userEmail: string,
    subscriptions: WebSocketSubscription[]
  ) {
    this.userEmail = userEmail;
    this.pendingSubscriptions = subscriptions;

    // Désabonner tous les topics actuels
    this.subscriptions.forEach((subscription, topic) => {
      this.unsubscribeFromTopic(topic);
    });

    // S'abonner aux nouveaux topics activés
    subscriptions.forEach(({ topic, enabled }) => {
      if (enabled && this.messageCallbacks.has(topic)) {
        const callback = this.messageCallbacks.get(topic)!;
        this.subscribeToTopic(userEmail, topic, callback);
      }
    });
  }

  isTopicSubscribed(topic: string): boolean {
    return this.subscriptions.has(topic);
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }
}

export const webSocketService = new WebSocketService();
